import { SiteUrls } from '@tableau/shared-urls';
import { TextAreaWidget } from '@tableau/widgets';
import * as React from 'react';
import * as _ from 'underscore';
import { Formatters, Messages } from '../../../../codegen/Localize';
import { CommonStyles } from '../../../../common/react/CommonStyles';
import { cssClass } from '../../../../style';
import { DataRoleFieldSynonym, updateDataroleApi } from '../../../apis/UpdateDatarole';
import { sectionHeaderClass, sectionTitleClass } from '../../../styles/DefinitionSubspaceSharedStyles';
import { DataType } from '../../../types/DetailEnums';
import { EnumChipPicker } from '../../../utils/chip-pickers/EnumChipPicker';
import { SynonymsChipPicker } from '../../../utils/chip-pickers/SynonymChipPicker';
import { friendlyDataType, getFriendlyDataType } from '../../../utils/contentMaps';
import { DescriptorBlock, DescriptorBlockChildProps } from './DescriptorBlock';

export interface DefinitionSubspaceAboutSectionProps {
  readonly siteUrls: SiteUrls;
  readonly luid: string;
  readonly dataTypes: ReadonlyArray<DataType>;
  readonly description: string;
  readonly synonyms: ReadonlyArray<DataRoleFieldSynonym>;
}

const cssPrefix = 'DefinitionSubspaceAboutSection';
const rowClass = cssClass(cssPrefix, 'topRow', {display: 'flex', flexWrap: 'wrap', paddingLeft: CommonStyles.Sizes.DefaultSpace});
const descriptionEdit = cssClass(cssPrefix, 'descriptionEdit', {
  width: '100%',
  height: '6em',
  $nest: {
    'textarea': {
      resize: 'none',
      width: '100%',
      height: '100%',
    }
  }});

export const DefinitionSubspaceAboutSection: React.StatelessComponent<DefinitionSubspaceAboutSectionProps> = (props: DefinitionSubspaceAboutSectionProps) => {
  return <section>
    <header className={sectionHeaderClass}><h3 className={sectionTitleClass}>{Messages.dataRoleDefinitionSubspace_About_Title()}</h3></header>
    <div className={rowClass} >
      <DescriptorBlock
        title={Messages.dataRoleDefinitionSubspace_About_RoleType()}
        content={Messages.dataRoleDefinitionSubspace_About_RoleType_Dictionary()}/>
      <DescriptorBlock
        title={Messages.dataRoleDefinitionSubspace_About_DataType()}
        content={props.dataTypes}
        contentDisplay={displayDataTypes}
        emptyMessage={Messages.dataRoleDefinitionSubspace_About_DataType_None()}
        />
      <DescriptorBlock
        title={Messages.dataRoleDefinitionSubspace_About_FieldSynonyms()}
        content={props.synonyms}
        contentDisplay={displayFieldSynonyms}
        emptyMessage={Messages.dataRoleDefinitionSubspace_About_FieldSynonyms_None()}
        onSave={saveSynonymsEdit(props.luid)}
        editable
        />
    </div>
    <div className={rowClass} >
      <DescriptorBlock
        title={Messages.dataRoleDefinitionSubspace_About_Description()}
        content={props.description}
        contentDisplay={displayDescription}
        emptyMessage={Messages.dataRoleDefinitionSubspace_About_Description_None()}
        onSave={saveDescriptionEdit(props.luid)}
        containerStyle={{flexGrow:1, maxWidth: '800px'}}
        editable
        />
    </div>
  </section>;
};

const displayDataTypes = (props: DescriptorBlockChildProps<DataType[]>) => {
  if (!props.isEditing) { return props.value.length > 0 ? <>{props.value.map(getFriendlyDataType).join(', ')}</> : null; }
  return <EnumChipPicker
    placeholder={Messages.dataRoleDefinitionSubspace_About_DataType_Placeholder()}
    testId="data-type-chip-picker"
    values={props.value}
    enumValues={Object.keys(friendlyDataType())}
    friendlyNames={friendlyDataType()}
    onChange={props.onChange}
    label={Messages.dataRoleDefinitionSubspace_About_DataType()}
    style={{maxWidth: '300px'}}/>;
};

const displayFieldSynonyms = (props: DescriptorBlockChildProps<DataRoleFieldSynonym[]>): React.ReactElement => {
  const valueStrings = props.value.filter(x => !x.is_primary).map(x => x.name);
  if (!props.isEditing) { return valueStrings.length > 0 ? <>{valueStrings.join(', ')}</> : null; }
  return <SynonymsChipPicker
    placeholder={Messages.dataRoleDefinitionSubspace_About_FieldSynonyms_Placeholder()}
    testId="field-synonym-chip-picker"
    values={valueStrings}
    onChange={(newValueStrings: string[]) => props.onChange(augmentSynonymsWithList(props.value, newValueStrings))}
    label={Messages.dataRoleDefinitionSubspace_About_FieldSynonyms()}
    style={{maxWidth: '300px'}}
    getErrorMessage={validateSynonym}/>;
};

const displayDescription = (props: DescriptorBlockChildProps<string>) => {
  if (!props.isEditing) { return props.value && <>{props.value}</>; }
  return <TextAreaWidget
    testId="description-edit"
    label={undefined}
    maxLength={4000}
    onChange={(e) => props.onChange(e.target.value)}
    defaultValue={props.value}
    className={descriptionEdit}/>;
};

const saveDescriptionEdit = (luid: string) => (newDescription: string) => {
  updateDataroleApi(luid, {description: newDescription})
    .catch(_.noop);
};

const saveSynonymsEdit = (luid: string) => (names: DataRoleFieldSynonym[]) => {
  updateDataroleApi(luid, {
    field_concept:{ names },
  })
  .catch(_.noop);
};


const validateSynonym = (synonym: string): string => {
  if (synonym.length > 255) { return Messages.dataRoleDefinitionSubspace_About_FieldSynonyms_Error_TooLong({MAX_LENGTH: Formatters.formatCount(255) }); }
  return '';
};

// Make sure that every new string not corresponding to a synonym in originalValues gets a FieldSynonym object
const augmentSynonymsWithList = (originalValues: DataRoleFieldSynonym[], newStrings: string[]): DataRoleFieldSynonym[] => {
  const primaryValues = originalValues.filter(value => value.is_primary);
  const newValues = newStrings.map(name => originalValues.find(value => value.name === name) || {name, locale: '', weight: 1, is_primary: false});
  return [...primaryValues, ...newValues];
};
